import {
  BMComponentsType,
  BMPrimitives,
  BMText,
  useBMTheme,
} from "@traveloka/district-bloom";
import { useCallback, useMemo, useState } from "react";
import {
  Image,
  LayoutAnimation,
  Platform,
  StyleSheet,
  UIManager,
} from "react-native";

import FacilityRow from "./FacilityRow";

import BMView from "../../../../components/View/BMView";
import { useMobileContentResource } from "../../../../core/resource/MobileResourceProvider";
import { AirlineSummary } from "../../FlightSearch/types";
import { EXTRA_FACILITIES, PRIMARY_FACILITIES } from "./constants";
import { ExtraFacilitiesToggle } from "./ExtraFacilitiesToggle";
import { filterFacilities, getFacilityCatalog } from "./utils";

if (
  Platform.OS === "android" &&
  UIManager.setLayoutAnimationEnabledExperimental
) {
  UIManager.setLayoutAnimationEnabledExperimental(true);
}

type Props = {
  airlines: AirlineSummary;
  seatClass: string;
};

export default function FlightFacilitiesInfo({
  airlines: { image, flightCode, name, facilities },
  seatClass,
}: Props) {
  const { BMComponents } = useBMTheme();
  const { CorporateFlightProductSummary: cr } = useMobileContentResource();
  const styles = createStyles(BMComponents);

  const [hideExtraFacilities, setHideExtraFacilities] = useState(true);

  const toggleExtra = useCallback(() => {
    LayoutAnimation.configureNext({
      duration: 280,
      create: { type: "easeInEaseOut", property: "opacity" },
      update: { type: "easeInEaseOut", property: "opacity" },
      delete: { type: "easeInEaseOut", property: "opacity" },
    });
    setHideExtraFacilities((s) => !s);
  }, []);

  const [primaryFacilities, extraFacilities] = useMemo(() => {
    if (!facilities?.length) return [[], []];
    const catalog = Object.values(getFacilityCatalog(facilities, cr));
    const primary = filterFacilities(catalog, PRIMARY_FACILITIES);
    const extra = filterFacilities(catalog, EXTRA_FACILITIES);
    return [primary, extra];
  }, [facilities, cr]);

  const renderedFacilities = useMemo(
    () =>
      hideExtraFacilities
        ? primaryFacilities
        : [...primaryFacilities, ...extraFacilities],
    [hideExtraFacilities, primaryFacilities, extraFacilities]
  );

  return (
    <BMView style={styles.container}>
      <BMView style={styles.content} spacing="sm">
        <BMView row spacing="md" align="center">
          <BMText variant="uiLarge">{name}</BMText>
          <Image
            source={{
              uri: image.src,
            }}
            style={styles.airlinesImg}
            resizeMode="contain"
          />
        </BMView>
        <BMText variant="uiLargeLi">
          {flightCode}
          {seatClass ? ` • ${seatClass}` : ""}
        </BMText>
      </BMView>
      {!!facilities.length && (
        <>
          <BMView style={styles.divider} />
          <BMView style={styles.content} spacing="sm">
            {renderedFacilities.map((item) => (
              <FacilityRow key={item.type} item={item} />
            ))}
          </BMView>
          {!!extraFacilities.length && (
            <ExtraFacilitiesToggle
              extraFacilities={extraFacilities}
              hideExtraFacilities={hideExtraFacilities}
              toggleExtra={toggleExtra}
            />
          )}
        </>
      )}
    </BMView>
  );
}

function createStyles(BMComponents: BMComponentsType) {
  return StyleSheet.create({
    container: {
      borderRadius: BMPrimitives.BMRadius.md,
      borderWidth: BMPrimitives.BMBorder.width.thick,
      borderColor: BMComponents.BMStyleLineBordercolorSubtle,
      backgroundColor: BMComponents.BMStyleSurfaceBgcolorInventory,
    },
    content: {
      padding: BMPrimitives.BMSize.md,
    },
    divider: {
      borderTopWidth: BMPrimitives.BMBorder.width.thick,
      borderColor: BMComponents.BMStyleLineBordercolorSubtle,
    },
    airlinesImg: {
      width: BMPrimitives.BMSize.xl,
      height: BMPrimitives.BMSize.xl,
    },
  });
}
